import React from 'react';
import Button from '@material-ui/core/Button';
import Link from '@material-ui/core/Link';
import TextField from '@material-ui/core/TextField';
import { withTranslation, WithTranslation } from 'react-i18next';
import { Link as RouterLink, RouteComponentProps } from 'react-router-dom';
import { connect } from 'react-redux';
import EuroIcon from '@material-ui/icons/Euro';
// Components
import App from '../../../components/app';
import { Dialog } from '../../../components/dialog';
import { ActionFunction, FormatType, HeadCell, PickerOption } from '../../../local-types';

import EnhancedTable from '../../../components/table';
import { TooltipButton } from '../../../components/tooltip-button';
import ErrorMessage from '../../../components/error-message';
// Modules
import { tableRequestPoints } from '../../../redux/reducers/table';
import { actions as billActions } from '../../../redux/reducers/bills';
import { ErrorPayload, selectors as errorSelectors } from '../../../redux/reducers/errors';
// ressources
import FormattingUtils from '../../../utils/formattingUtils';
import withGlobalStyles from '../../../utils/global-styles';
import DateUtils from '../../../utils/dateUtils';
import UserUtils, { AccountRight, RightRank } from '../../../utils/userUtils';
import { paths } from '../../paths';
import { Cell, createCell } from '../../../components/table/table-cell-creator';

const queryString = require('query-string');

interface RectificationBillProps extends WithTranslation, RouteComponentProps {
    globalClasses: any;
    setGroupedPaiementError: ErrorPayload;
    setPaiement: (id: string, flag: string, amount: number, callback?: React.EffectCallback) => any;
}

class RectificationBillListPage extends React.Component<RectificationBillProps> {
    actions: ActionFunction[];
    tableRef: any;
    filterParams: any;
    statusOptions: PickerOption[];
    paiementOptions: PickerOption[];

    isAdmin: boolean;

    constructor(props: RectificationBillProps) {
        super(props);
        this.filterParams = queryString.parse(this.props.location.search);
        this.isAdmin = UserUtils.checkRightsOrAdmin({ name: AccountRight.BILL_RECTIFICATION, mode: RightRank.W });
        const { t } = props;
        this.statusOptions = [
            { value: '', label: '' },
            { value: 'paid', label: t('common:true') },
            { value: 'payment_manual', label: t('common:false') },
        ];
        this.paiementOptions = [
            { value: '', label: '' },
            { value: 'wire_transfer', label: t('common:paiement.wire_transfer') },
            { value: 'sepa', label: t('common:paiement.sepa') },
            { value: 'card_adyen', label: t('common:paiement.card_adyen') },
        ];
    }

    handleAction = (row, fields): void => {
        const { price } = fields;
        const { id, amount_with_tax } = row;
        const remaining = Math.max(amount_with_tax - price, 0);
        const flag = price >= amount_with_tax ? 'paid' : 'payment_manual';
        this.props.setPaiement(id, flag, remaining, () => {
            this.tableRef.reload();
        });
    };

    headerBuilder = (): HeadCell[] => {
        const { t, globalClasses } = this.props;

        const headers: HeadCell[] = [
            createCell(t, Cell.BILL_NUMBER),
            createCell(t, Cell.BILL_DOCUMENT_PATH),
            {
                id: 'original_bill_number',
                label: t('bill_id'),
                filterable: false,
                sortable: false,
                formatCell: function (row) {
                    const { original_bill_number, start_date } = row;
                    return (
                        original_bill_number && (
                            <Link
                                color="secondary"
                                component={RouterLink}
                                to={`${paths.billsPeriods}?filters[bill_number]=${original_bill_number}&after[created_at]=${start_date.split('T')[0]}`}
                            >
                                {original_bill_number}
                            </Link>
                        )
                    );
                },
            },
            createCell(t, Cell.BILL_CREATED_AT),
            {
                id: 'start_date',
                label: t('facturation_period'),
                filterable: false,
                formatCell: function (row) {
                    return `${DateUtils.tsToDate(row.start_date, true)} - ${DateUtils.tsToDate(row.end_date, true)}`;
                },
            },
            createCell(t, Cell.COMPANY_ID),
            {
                id: 'status',
                label: t('common:table.paid'),
                type: FormatType.Picker,
                sortable: false,
                pickerOptions: this.statusOptions,
                formatCell: function (row) {
                    let res = t('common:true');
                    if (row.status !== 'paid') {
                        res = t('common:false');
                    }
                    return res;
                },
            },
            {
                id: 'remaining_balance',
                label: t('remaining_balance'),
                type: FormatType.Price,
                sortable: false,
                filterable: false,
                formatCell: function (row) {
                    let res = FormattingUtils.price(0, true);
                    if (row.status !== 'paid') {
                        const remaining =
                            row.payment_type !== 'card_adyen' ? row.remaining_balance : row.amount_with_tax;
                        res = FormattingUtils.price(remaining);
                    }
                    return res;
                },
            },
            // createCell(t, Cell.AMOUNT_PRE_TAX),
            createCell(t, Cell.AMOUNT_WITH_TAX),
            {
                id: 'payment_type',
                label: t('payment_type'),
                sortable: false,
                type: FormatType.Picker,
                pickerOptions: this.paiementOptions,
                formatCell: function (row) {
                    return t('common:paiement.' + row.payment_type);
                },
            },
        ];

        if (this.isAdmin) {
            headers.push({
                id: 'credit',
                label: t('common:action'),
                filterable: false,
                formatCell: (row) => {
                    return (
                        row.status !== 'paid' &&
                        row.payment_type !== 'card_adyen' && (
                            <Dialog
                                btnTitle={t('common:table.paid')}
                                description={t('paid_description', { amount: row.amount_with_tax })}
                                customButton={
                                    <TooltipButton title={t('addPaidAmount')}>
                                        <Button
                                            size="small"
                                            variant="contained"
                                            color="primary"
                                            aria-label="action"
                                            style={{ marginRight: 8 }}
                                            className={globalClasses.btnAutoWidth}
                                        >
                                            <EuroIcon fontSize="small" />
                                        </Button>
                                    </TooltipButton>
                                }
                                formComponent={
                                    <TextField
                                        autoFocus
                                        margin="dense"
                                        id="price"
                                        label={t('paid_placeholder')}
                                        type="number"
                                        name="price"
                                        required={true}
                                        fullWidth
                                        inputProps={{ step: 0.01, min: 0 }}
                                    />
                                }
                                onOK={(fields) => this.handleAction(row, fields)}
                            />
                        )
                    );
                },
            });
        }

        return headers;
    };

    render() {
        const { t, setGroupedPaiementError } = this.props;

        return (
            <App
                needRights={{ name: AccountRight.BILL_RECTIFICATION }}
                disableBreadcrumb
                header={{
                    title: t('title_rectification'),
                }}
            >
                <ErrorMessage payload={setGroupedPaiementError} />
                <EnhancedTable
                    onRef={(ref) => (this.tableRef = ref)}
                    headers={this.headerBuilder()}
                    type={tableRequestPoints.BILLS_RECTIFICATIONS}
                    filterParams={this.filterParams}
                />
            </App>
        );
    }
}

const RectificationBillListPageWithStyleAndTranslation = withGlobalStyles(
    withTranslation(['bill', 'common'])(RectificationBillListPage),
);

const mapStateToProps = (state) => ({
    setGroupedPaiementError: errorSelectors.setGroupedPaiementError(state),
});

const mapDispatchToProps = {
    setPaiement: billActions.setPaiement,
};

export default connect(mapStateToProps, mapDispatchToProps)(RectificationBillListPageWithStyleAndTranslation);
